import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AxiosResponse } from "axios";
import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { GetTabHistory } from "Services/MicrobiologyResultData";
import useLang from "Shared/hooks/useLanguage";

interface LookupOptions {
  colonyCounts?: any[];
  cultureGrowths?: any[];
  gramStains?: any[];
  sources?: any[];
  organisms?: any[];
  cannedComments?: any[];
}

interface ViewHistoricalDataModalProps {
  show: boolean;
  onHide: () => void;
  reqOrderId?: number;
  tabId?: number;
  lookupOptions?: LookupOptions;
}

interface TabHistoryRecord {
  tabId: number;
  tabName: string;
  orderResultInfoDatas?: any[];
  sentivityResultData?: any[];
}

const displayValue = (value: any) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const getOptionId = (option: any) => {
  if (option == null || typeof option !== "object") return undefined;
  return option.value ?? option.id ?? option.key;
};

const getOptionLabel = (option: any) => {
  if (option == null || typeof option !== "object") return undefined;
  return option.label ?? option.name ?? option.text ?? option.description;
};

const resolveLookupLabel = (options: any[] | undefined, id: any) => {
  if (id === null || id === undefined || id === "") return "-";
  const match = options?.find((option) => getOptionId(option) == id);
  const label = match ? getOptionLabel(match) : undefined;
  return label ?? String(id);
};

const HistoricalSummaryCard = ({
  summary,
  lookupOptions,
}: {
  summary: any;
  lookupOptions?: LookupOptions;
}) => {
  const { t } = useLang();
  const plateDescriptions = Array.isArray(summary.plateDescriptions) ? summary.plateDescriptions : [];

  return (
    <div className="card rounded mb-3" style={{ border: "2px solid #ffc107" }}>
      <div className="card-header px-4 d-flex justify-content-between align-items-center rounded bg-light-warning min-h-40px">
        <h6 className="h6 mb-0">{displayValue(summary.panelName)}</h6>
      </div>
      <div className="card-body py-3 px-4">
        <div className="row g-3 mb-3">
          <div className="col-md-3 col-sm-6">
            <span className="text-muted fs-7 d-block">{t("Colony Count")}</span>
            <span className="fw-semibold">{resolveLookupLabel(lookupOptions?.colonyCounts, summary.colonyId)}</span>
          </div>
          <div className="col-md-3 col-sm-6">
            <span className="text-muted fs-7 d-block">{t("Culture Growth")}</span>
            <span className="fw-semibold">{resolveLookupLabel(lookupOptions?.cultureGrowths, summary.cultureGrowthId)}</span>
          </div>
          <div className="col-md-3 col-sm-6">
            <span className="text-muted fs-7 d-block">{t("Gram Stain")}</span>
            <span className="fw-semibold">{resolveLookupLabel(lookupOptions?.gramStains, summary.gramStainId)}</span>
          </div>
          <div className="col-md-3 col-sm-6">
            <span className="text-muted fs-7 d-block">{t("Source")}</span>
            <span className="fw-semibold">{resolveLookupLabel(lookupOptions?.sources, summary.sourceId)}</span>
          </div>
        </div>

        {plateDescriptions.length > 0 && (
          <>
            <h6 className="text-primary h6 mb-2">{t("Plate Description")}</h6>
            <div className="row g-2 mb-3">
              {plateDescriptions.map((pd: any, idx: number) => (
                <div key={pd.id || pd.descId || idx} className="col-md-6 col-12">
                  <div className="border rounded px-3 py-2 bg-light">
                    <span className="text-muted fs-7 d-block">{pd.label || t("Description")}</span>
                    <span className="fw-semibold">{displayValue(pd.value ?? pd.description)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="row g-3">
          <div className="col-md-6 col-12">
            <span className="text-muted fs-7 d-block">{t("Internal Comments")}</span>
            <div className="border rounded px-3 py-2 bg-white min-h-50px">
              {displayValue(summary.internalComments)}
            </div>
          </div>
          <div className="col-md-6 col-12">
            <span className="text-muted fs-7 d-block">{t("Significant Findings")}</span>
            <div className="border rounded px-3 py-2 bg-white min-h-50px">
              {displayValue(summary.significantFinding)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HistoricalSensitivityCard = ({
  panel,
  lookupOptions,
}: {
  panel: any;
  lookupOptions?: LookupOptions;
}) => {
  const { t } = useLang();
  const rows = Array.isArray(panel.sensitiResultData) ? panel.sensitiResultData : [];
  const selectedCount = rows.filter((row: any) => row.isSelected).length;

  return (
    <div className="card rounded mb-3" style={{ border: "1px solid #dee2e6" }}>
      <div className="card-header px-4 bg-light min-h-40px d-flex align-items-center justify-content-between">
        <h6 className="h6 mb-0">{displayValue(panel.panelName)}</h6>
        <span className="badge badge-light-primary fs-8">
          {selectedCount} {t("selected")}
        </span>
      </div>
      <div className="card-body px-4 pb-3">
        <div className="row g-3 mb-3">
          <div className="col-md-6 col-12">
            <span className="text-muted fs-7 d-block">{t("Organism Name")}</span>
            <span className="fw-semibold">{resolveLookupLabel(lookupOptions?.organisms, panel.organismId)}</span>
          </div>
          <div className="col-md-6 col-12">
            <span className="text-muted fs-7 d-block">{t("Canned Comments")}</span>
            <span className="fw-semibold">{resolveLookupLabel(lookupOptions?.cannedComments, panel.cannedCommentId)}</span>
          </div>
        </div>

        {rows.length > 0 ? (
          <div className="table_bordered overflow-hidden">
            <TableContainer component={Paper} className="shadow-none" sx={{ maxHeight: 320 }}>
              <Table
                stickyHeader
                size="small"
                className="table table-bordered table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
              >
                <TableHead>
                  <TableRow className="h-30px">
                    <TableCell className="fw-bold" sx={{ width: 48 }} />
                    <TableCell className="fw-bold">{t("Antimicrobial")}</TableCell>
                    <TableCell className="fw-bold" sx={{ width: "25%" }}>{t("MIC")}</TableCell>
                    <TableCell className="fw-bold" sx={{ width: "25%" }}>{t("Interpretation")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row: any) => (
                    <TableRow
                      key={row.id}
                      className="h-30px"
                      sx={{
                        backgroundColor: row.isSelected ? "rgba(40, 167, 69, 0.06)" : "inherit",
                      }}
                    >
                      <TableCell sx={{ width: 48 }}>
                        {row.isSelected ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                        )}
                      </TableCell>
                      <TableCell>{row.antimicrobial}</TableCell>
                      <TableCell>{displayValue(row.mic)}</TableCell>
                      <TableCell>{displayValue(row.interpretation)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ) : (
          <p className="text-muted mb-0">{t("No sensitivity data available")}</p>
        )}
      </div>
    </div>
  );
};

const TabHistoryContent = ({
  record,
  lookupOptions,
  defaultExpanded = true,
}: {
  record: TabHistoryRecord;
  lookupOptions?: LookupOptions;
  defaultExpanded?: boolean;
}) => {
  const { t } = useLang();
  const summaries = Array.isArray(record.orderResultInfoDatas) ? record.orderResultInfoDatas : [];
  const sensitivityPanels = Array.isArray(record.sentivityResultData) ? record.sentivityResultData : [];
  const hasData = summaries.length > 0 || sensitivityPanels.length > 0;

  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{ border: "1px solid #e4e6ef", borderRadius: "6px !important", "&:before": { display: "none" }, mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f5f8fa", minHeight: 48 }}>
        <div className="d-flex align-items-center gap-2">
          <span className="badge badge-light-info">{record.tabName || t("Tab")}</span>
          <span className="text-muted fs-7">
            {summaries.length} {t("result summary record(s)")}, {sensitivityPanels.length} {t("sensitivity panel(s)")}
          </span>
        </div>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2 }}>
        {!hasData ? (
          <p className="text-muted mb-0 text-center py-3">{t("No data recorded for this tab")}</p>
        ) : (
          <>
            {summaries.length > 0 && (
              <div className="mb-4">
                <h6 className="text-primary h6 mb-3">{t("Results Summary")}</h6>
                {summaries.map((summary: any, idx: number) => (
                  <HistoricalSummaryCard
                    key={summary.id || idx}
                    summary={summary}
                    lookupOptions={lookupOptions}
                  />
                ))}
              </div>
            )}
            {sensitivityPanels.length > 0 && (
              <div>
                <h6 className="text-primary h6 mb-3">{t("Sensitivity Data")}</h6>
                {sensitivityPanels.map((panel: any, idx: number) => (
                  <HistoricalSensitivityCard
                    key={panel.id || idx}
                    panel={panel}
                    lookupOptions={lookupOptions}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const ViewHistoricalDataModal: React.FC<ViewHistoricalDataModalProps> = ({
  show,
  onHide,
  reqOrderId,
  tabId,
  lookupOptions,
}) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [historyRecords, setHistoryRecords] = useState<TabHistoryRecord[]>([]);

  const fetchHistoricalData = useCallback(async () => {
    if (!reqOrderId || tabId === undefined) {
      toast.error(t("Missing required data for historical data"));
      return;
    }

    setLoading(true);
    try {
      const res: AxiosResponse = await GetTabHistory({
        reqOrderId: reqOrderId,
        tabId: tabId,
      });

      if (res?.data?.statusCode === 200) {
        const data = res?.data?.data;
        setHistoryRecords(Array.isArray(data) ? data : data ? [data] : []);
      } else {
        toast.error(t(res?.data?.message || "Failed to load historical data"));
        setHistoryRecords([]);
      }
    } catch (err: any) {
      console.error("Error fetching historical data:", err);
      toast.error(t(err?.response?.data?.message || "Error loading historical data"));
      setHistoryRecords([]);
    } finally {
      setLoading(false);
    }
  }, [reqOrderId, tabId, t]);

  useEffect(() => {
    if (show && reqOrderId && tabId !== undefined) {
      fetchHistoricalData();
    } else if (!show) {
      setHistoryRecords([]);
    }
  }, [show, reqOrderId, tabId, fetchHistoricalData]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      size="xl"
      scrollable
      centered
    >
      <Modal.Header className="py-3 d-flex justify-content-between align-items-center">
        <Modal.Title className="h5 fw-bold mb-0">{t("Historical Data")}</Modal.Title>
        <IconButton onClick={onHide} aria-label="close" size="small">
          <CloseIcon />
        </IconButton>
      </Modal.Header>
      <Divider />
      <Modal.Body className="py-4 px-4" style={{ maxHeight: "75vh" }}>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t("Loading...")}</span>
            </div>
          </div>
        ) : historyRecords.length > 0 ? (
          historyRecords.map((record, idx) => (
            <TabHistoryContent
              key={`${record.tabId}-${idx}`}
              record={record}
              lookupOptions={lookupOptions}
              defaultExpanded={historyRecords.length === 1 || idx === 0}
            />
          ))
        ) : (
          <div className="text-center py-5">
            <p className="text-muted mb-0">{t("No historical data available")}</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2 border-0">
        <button type="button" className="btn btn-sm btn-secondary" onClick={onHide}>
          {t("Close")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewHistoricalDataModal;
